import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '../../../../library/button/button';
import InputGroup from '../../../../library/input-group/input-group';
import Link from '../../../../library/link/link';
import Typography from '../../../../library/typography/typography';
import FitmentTireCodeDescription from '../../../../components/fitment-tire-code-description/fitment-tire-code-description';
import Modal from '../../../../components/modal/modal';
import CanonicalFormField from '../canonical-form-field/canonical-form-field';

import { useContainerQuery, useFitment } from '../../../../shared/hooks/hooks';
import useCanonicalDefaultProduct from '../../hooks/use-canonical-default-product/use-canonical-default-product';
import useCanonicalForm from '../../hooks/use-canonical-form/use-canonical-form';

import {
  getNormalizedProductType,
  isCanonicalProduct
} from '../../../../shared/utils/product/product';
import { getCompletedCanonicalFilterSelections } from '../../../../shared/utils/product-detail-utils/product-detail-utils';
import { ProductProp } from '../../../../shared/utils/prop-types/prop-types';

import { TIRE_FILTER_KEYS as FILTER_KEYS } from '../../../../shared/constants/filters';
import { PRODUCT_TYPES } from '../../../../shared/constants/products';

import styles from './canonical-tire-form.scss';

const MODAL_TYPES = {
  LOAD_INDEX: 'LOAD_INDEX',
  LOAD_RANGE: 'LOAD_RANGE',
  OE: 'OE',
  SIDEWALL: 'SIDEWALL',
  VPN: 'VPN'
};

function CanonicalTireForm(props) {
  const { isLoading, onFilterChange, product } = props;

  const canonicalForm = useCanonicalForm(product, PRODUCT_TYPES.TIRES);
  const { shouldIgnoreDefaultProduct } = useCanonicalDefaultProduct(product);
  const { selectedFitment } = useFitment();
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [modalType, setModalType] = useState(MODAL_TYPES.LOAD_INDEX);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const canonicalFormRef = useRef(canonicalForm);
  const breakpoint = useContainerQuery();

  const isCanonical = isCanonicalProduct(product);
  const productType = getNormalizedProductType(product);
  const { filters, onSelectFilter, queryFilters, selectedFilters } =
    canonicalForm;
  const serializedFilters = JSON.stringify(selectedFilters);

  canonicalFormRef.current = canonicalForm;

  function toggleHelp() {
    setIsHelpOpen(!isHelpOpen);
  }

  function openModal(type) {
    setModalType(type);
    setIsModalOpen(true);
  }

  function closeModal() {
    setIsModalOpen(false);
  }

  function getOptions(name) {
    let options = null;

    if (filters[name]) {
      options = filters[name];
    } else if (selectedFilters[name]) {
      options = [selectedFilters[name]];
    }

    return options;
  }

  function getValue(name) {
    let value = null;
    if (selectedFilters[name]) {
      value = selectedFilters[name];
    } else if (!isCanonical && productType === PRODUCT_TYPES.TIRES) {
      let key = name;

      if (name === FILTER_KEYS.RIM_DIAMETER) {
        key = 'tireWheelRimDiameter';
      } else if (name === FILTER_KEYS.SIDEWALL_DESCRIPTION) {
        key = 'sideWallDescriptionName';
      } else if (name === FILTER_KEYS.ASPECT_RATIO) {
        key = 'tireAspectRatio';
      }
      const productVal = product[key];

      if (productVal) {
        value = { label: productVal, value: productVal };
      }
    }

    return value;
  }

  function handleSelection(key, value) {
    if (typeof onSelectFilter === 'function' && key && value) {
      onSelectFilter(
        { [key]: { ...value, wasAutoSelected: false } },
        value.shouldResetFilter
      );
    }
  }

  useEffect(() => {
    const isCanonical = isCanonicalProduct(product);
    const productType = getNormalizedProductType(product);
    const { filters, onComplete, onSelectFilter, selectedFilters } =
      canonicalFormRef.current;
    const hasDefaultProduct = Boolean(product?.aggregates?.defaultProduct);
    const shouldUseDefaultProduct =
      isCanonical &&
      !shouldIgnoreDefaultProduct &&
      hasDefaultProduct &&
      filters[FILTER_KEYS.CROSS_SECTION]?.length === 1 &&
      filters[FILTER_KEYS.ASPECT_RATIO]?.length === 1 &&
      filters[FILTER_KEYS.RIM_DIAMETER]?.length === 1;

    if (
      !isLoading &&
      (!isCanonical || shouldUseDefaultProduct) &&
      productType === PRODUCT_TYPES.TIRES
    ) {
      const srcProduct = shouldUseDefaultProduct
        ? product.aggregates.defaultProduct
        : product;
      const {
        loadIndexWithRatingKey,
        loadRange,
        origEquipment2,
        sideWallDescriptionName
      } = srcProduct || {};

      const tireCrossSection = srcProduct?.tireCrossSection?.toString();
      const tireWheelRimDiameter = srcProduct?.tireWheelRimDiameter?.toString();
      const tireAspectRatio = srcProduct?.tireAspectRatio?.toString();

      const completedFilters = getCompletedCanonicalFilterSelections(
        [
          { key: FILTER_KEYS.CROSS_SECTION, value: tireCrossSection },
          { key: FILTER_KEYS.ASPECT_RATIO, value: tireAspectRatio },
          { key: FILTER_KEYS.RIM_DIAMETER, value: tireWheelRimDiameter },
          { key: FILTER_KEYS.LOAD_INDEX, value: loadIndexWithRatingKey },
          { key: FILTER_KEYS.LOAD_RANGE, value: loadRange },
          {
            key: FILTER_KEYS.SIDEWALL_DESCRIPTION,
            value: sideWallDescriptionName
          },
          { key: FILTER_KEYS.ORIGINAL_EQUIPMENT, value: origEquipment2 }
        ],
        selectedFilters
      );
      const completedFilterKeys = Object.keys(completedFilters);

      if (shouldUseDefaultProduct) {
        if (completedFilterKeys.length > 0) {
          const filterKey = completedFilterKeys[0];
          onSelectFilter({ [filterKey]: completedFilters[filterKey] }, false);
        }
      } else {
        onComplete(completedFilters, false);
      }
    }
  }, [product, serializedFilters, isLoading, shouldIgnoreDefaultProduct]);

  useEffect(() => {
    onFilterChange(queryFilters);
  }, [queryFilters, onFilterChange]);

  return (
    <form className={styles['container']}>
      <InputGroup columnCount={1}>
        {filters[FILTER_KEYS.CROSS_SECTION]?.length === 1 &&
        filters[FILTER_KEYS.ASPECT_RATIO]?.length === 1 &&
        filters[FILTER_KEYS.RIM_DIAMETER]?.length === 1 ? (
          <>
            {!selectedFitment && !isLoading && product && (
              <div>
                <Typography className={styles['tire-size']} tag="div">
                  Tire Size:
                </Typography>
                <strong>{product.description?.split(' ')[0]}</strong>
              </div>
            )}
          </>
        ) : (
          <div>
            <Typography className={styles['label']} tag="span">
              Tire Size:
            </Typography>
            <InputGroup columnCount={breakpoint.isMinLg ? 3 : 1}>
              <CanonicalFormField
                hideInfoButton
                label="Width"
                onChange={value => {
                  handleSelection(FILTER_KEYS.CROSS_SECTION, value);
                }}
                options={getOptions(FILTER_KEYS.CROSS_SECTION)}
                selectedValue={getValue(FILTER_KEYS.CROSS_SECTION)}
                showSingleOptionValues
              />

              <CanonicalFormField
                hideInfoButton
                label="Ratio"
                onChange={value => {
                  handleSelection(FILTER_KEYS.ASPECT_RATIO, value);
                }}
                options={getOptions(FILTER_KEYS.ASPECT_RATIO)}
                selectedValue={getValue(FILTER_KEYS.ASPECT_RATIO)}
                showSingleOptionValues
              />

              <CanonicalFormField
                hideInfoButton
                label="Diameter"
                onChange={value => {
                  handleSelection(FILTER_KEYS.RIM_DIAMETER, value);
                }}
                options={getOptions(FILTER_KEYS.RIM_DIAMETER)}
                selectedValue={getValue(FILTER_KEYS.RIM_DIAMETER)}
                showSingleOptionValues
              />
            </InputGroup>
            <Button
              className={styles['info-button']}
              onClick={toggleHelp}
              type="button"
            >
              help me choose
            </Button>

            <FitmentTireCodeDescription
              className="tire-sidewall"
              isOpen={isHelpOpen}
            />
          </div>
        )}

        <CanonicalFormField
          label="Load Index / Speed Rating"
          onChange={value => {
            handleSelection(FILTER_KEYS.LOAD_INDEX, value);
          }}
          onOpenModal={() => openModal(MODAL_TYPES.LOAD_INDEX)}
          options={getOptions(FILTER_KEYS.LOAD_INDEX)}
          selectedValue={getValue(FILTER_KEYS.LOAD_INDEX)}
          showSingleOptionValues={!selectedFitment}
        />

        <CanonicalFormField
          hasButtonOptions
          label="Load Range"
          onChange={value => {
            handleSelection(FILTER_KEYS.LOAD_RANGE, value);
          }}
          onOpenModal={() => openModal(MODAL_TYPES.LOAD_RANGE)}
          options={getOptions(FILTER_KEYS.LOAD_RANGE)}
          selectedValue={getValue(FILTER_KEYS.LOAD_RANGE)}
          showSingleOptionValues={!selectedFitment}
        />

        <CanonicalFormField
          label="Sidewall"
          onChange={value => {
            handleSelection(FILTER_KEYS.SIDEWALL_DESCRIPTION, value);
          }}
          onOpenModal={() => openModal(MODAL_TYPES.SIDEWALL)}
          options={getOptions(FILTER_KEYS.SIDEWALL_DESCRIPTION)}
          selectedValue={getValue(FILTER_KEYS.SIDEWALL_DESCRIPTION)}
          showSingleOptionValues={!selectedFitment}
        />

        <CanonicalFormField
          label="OE Designation"
          onChange={value => {
            handleSelection(FILTER_KEYS.ORIGINAL_EQUIPMENT, value);
          }}
          onOpenModal={() => openModal(MODAL_TYPES.OE)}
          options={getOptions(FILTER_KEYS.ORIGINAL_EQUIPMENT)}
          selectedValue={getValue(FILTER_KEYS.ORIGINAL_EQUIPMENT)}
          showSingleOptionValues={!selectedFitment}
        />

        <CanonicalFormField
          label="Vendor Product Number"
          onChange={value => {
            handleSelection(FILTER_KEYS.MANUFACTURER_AID, value);
          }}
          onOpenModal={() => openModal(MODAL_TYPES.VPN)}
          options={getOptions(FILTER_KEYS.MANUFACTURER_AID)}
          selectedValue={getValue(FILTER_KEYS.MANUFACTURER_AID)}
          showSingleOptionValues={!selectedFitment}
        />
      </InputGroup>

      {modalType === MODAL_TYPES.LOAD_INDEX && (
        <Modal
          heading="What Is Load Index / Speed Rating?"
          isOpen={isModalOpen}
          onClose={closeModal}
        >
          <strong>LOAD INDEX</strong>
          <p>
            This assigned number varies from 0 - 279, and corresponds to the
            tire&apos;s Load carrying capacity. For example - a 92 load index is
            equivalent to 1,389 lb load carrying capacity.
            <br />
            <Link target="_blank" to="/learn/load-range-load-index">
              Learn more about Load Index
            </Link>
          </p>

          <strong>SPEED RATING</strong>
          <p>
            A letter system identifying the maximum speed at which a tire can be
            driven, as determined by laboratory testing. For Example - H speed
            rating is rated up to 130 MPH
            <br />
            <Link target="_blank" to="/learn/speed-rating">
              Learn more about Speed Rating
            </Link>
          </p>
        </Modal>
      )}

      {modalType === MODAL_TYPES.LOAD_RANGE && (
        <Modal
          heading="What Is Load Range?"
          isOpen={isModalOpen}
          onClose={closeModal}
        >
          <strong>LOAD RANGE</strong>
          <p>
            Defines a range of maximum loads a tire can carry at a defined
            pressure. For example - C1 load range is a 6 ply rated tire at 50
            PSI.
            <br />
            <Link target="_blank" to="/learn/load-range-load-index">
              Learn more about Load Range
            </Link>
          </p>
        </Modal>
      )}

      {modalType === MODAL_TYPES.OE && (
        <Modal
          heading="What Is OE Designation?"
          isOpen={isModalOpen}
          onClose={closeModal}
        >
          <strong>OE DESIGNATION</strong>
          <p>
            The OE Designation means that the tire was built specifically for
            the manufacturer and has unique specification. For example: GM means
            the tire was built for General Motors like Buick, Chevrolet,
            Cadiallac, etc.
          </p>
          <p>
            Note: If you are replacing less than 4 tires, it is recommended to
            match the same OE tire designation.
          </p>
        </Modal>
      )}

      {modalType === MODAL_TYPES.SIDEWALL && (
        <Modal
          heading="What Is A Tire Sidewall?"
          isOpen={isModalOpen}
          onClose={closeModal}
        >
          <strong>TIRE SIDEWALL</strong>
          <p>
            The sidewall is the area of a tire that connects the tire bead and
            the tread. It protects the inner plies and showcases the tire name,
            size and type.
          </p>
        </Modal>
      )}

      {modalType === MODAL_TYPES.VPN && (
        <Modal
          heading="What Is Vendor Product Number?"
          isOpen={isModalOpen}
          onClose={closeModal}
        >
          <strong>VENDOR PRODUCT NUMBER</strong>
          <p>
            The vendor product number is the manufacturer&apos;s identification
            system. This number can be used to determine a specific product,
            down to the finest detail.
          </p>
        </Modal>
      )}
    </form>
  );
}

CanonicalTireForm.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  onFilterChange: PropTypes.func,
  product: ProductProp
};

export default CanonicalTireForm;
